import { useEffect, useMemo } from 'react'

// Components
import { DataTable } from 'components/molecules/DataTable/DataTable'
import { HeadCell } from 'components/molecules/DataTable/TableHeader'
import { FilterBar } from 'components/organisms/FilterBar/FilterBar'
import Header from 'components/molecules/Header/Header'

// MUI
import { Box } from '@mui/material'
// Services
import { useGetAssetReports } from 'lib/services/api/reports-service/assetReports'

// Context
import { useReportFiltersContext } from 'lib/context/ReportFiltersContext/ReportFiltersContext'

// Types
import { DefaultFilterParams } from 'lib/context/ReportFiltersContext/ReportFiltersContext.types'

// Other
import dayjs from 'lib/dayjs'
import { getAssetTypeLabel } from 'lib/utils/getAssetTypeLabel'
import { AssetTypeLabel, ImplantStatus } from 'common/types'
import { getAssetType } from 'lib/utils/getAssetType'
import {
  AssetReportOutput,
  AssetReportsInput,
  MappedAssets,
  ReportAsset,
} from './AssetsReports.types'
import { getReadableImplantStatus } from 'lib/utils/getReadableImplantStatus'
import { WASTED_REASON_LIST } from 'common/disposition'

import DownloadIcon from '@mui/icons-material/Download'
import { pdf } from '@react-pdf/renderer'
import AssetsReportPDF from 'views/AssetsReports/AssetsReportPDF'
import { saveAs } from 'file-saver'
import { enqueueSnackbar } from 'notistack'

const dateFormat = 'MM/DD/YYYY'
const headCells: HeadCell[] = [
  {
    id: 'date',
    label: 'Date/Time',
    numeric: true,
  },
  {
    id: 'location',
    label: 'Location',
    numeric: false,
  },
  {
    id: 'caseId',
    label: 'Case ID',
    numeric: true,
  },
  {
    id: 'patientName',
    label: 'Patient',
    numeric: false,
  },
  {
    id: 'procedureType',
    label: 'Procedure Type',
    numeric: false,
  },
  {
    id: 'surgeon',
    label: 'Surgeon',
    numeric: false,
  },
  {
    id: 'manufacturer',
    label: 'Manufacturer',
    numeric: false,
  },
  {
    id: 'productDescription',
    label: 'Product Description',
    numeric: false,
  },
  {
    id: 'modelNumber',
    label: 'Model Number',
    numeric: false,
  },
  {
    id: 'lot',
    label: 'Lot',
    numeric: false,
  },
  {
    id: 'serial',
    label: 'Serial',
    numeric: false,
  },
  {
    id: 'expiration',
    label: 'Expiration',
    numeric: false,
  },
  {
    id: 'udi',
    label: 'UDI',
    numeric: false,
  },
  {
    id: 'disposition',
    label: 'Disposition',
    numeric: false,
  },
  {
    id: 'wastedReason',
    label: 'Wasted Reason',
    numeric: false,
  },
  {
    id: 'assetType',
    label: 'Product Type',
    numeric: false,
  },
  {
    id: 'itemCost',
    label: 'Item Cost',
    numeric: true,
  },
]

const AssetsReports = () => {
  const { beforeDate, afterDate, filters, setFilters, setReportData } =
    useReportFiltersContext()

  const queryParams = useMemo(() => {
    const assetFilters = (filters as DefaultFilterParams) || {}
    const params = {} as AssetReportsInput
    if (assetFilters?.patientName) params.patientName = assetFilters.patientName
    if (assetFilters?.surgeonName) params.surgeonName = assetFilters.surgeonName
    if (assetFilters?.procedureType)
      params.procedureType = assetFilters.procedureType
    if (afterDate)
      params.startDate = afterDate.format(
        'YYYY-MM-DD'
      ) as unknown as dayjs.Dayjs
    if (beforeDate)
      params.endDate = beforeDate.format('YYYY-MM-DD') as unknown as dayjs.Dayjs
    if (assetFilters?.deviceDescription)
      params.deviceDescription = assetFilters.deviceDescription

    if (assetFilters?.disposition) {
      params.disposition =
        assetFilters.disposition.toUpperCase() as ImplantStatus
    }

    if (assetFilters.assetType && assetFilters.assetType !== 'Explanted') {
      params.assetType = getAssetType(assetFilters.assetType as AssetTypeLabel)
    }

    if (assetFilters.department) params.location = assetFilters.department

    params.pageSize = 1000
    params.status = 'SUBMITTED'

    return params
  }, [filters, afterDate, beforeDate])

  const { data, isLoading, isError } = useGetAssetReports({
    pageSize: 100,
    ...queryParams,
  })

  const assets = data as AssetReportOutput[]

  useEffect(() => {
    setFilters({
      open: false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (assets) {
      setReportData(assets)
    }
  }, [assets, setReportData])

  const getImplantStatus = (assets: ReportAsset[]) => {
    // This handles the case where the assets has many dispositions and we want to show the filtered one.
    if (queryParams.disposition) {
      if (assets?.length > 1) {
        return getReadableImplantStatus(
          assets?.find(
            (asset) => asset.implantStatus === queryParams.disposition
          )?.implantStatus as ImplantStatus
        ) as ImplantStatus
      }
    }

    return getReadableImplantStatus(
      assets?.[0]?.implantStatus as ImplantStatus
    ) as ImplantStatus
  }

  const getWastedReason = (assets: ReportAsset[]) => {
    let wastedReason = '-'
    if (assets?.length > 1) {
      wastedReason =
        assets?.find((asset) => asset.implantStatus === 'WASTED')
          ?.wastedReason ?? '-'
    } else {
      wastedReason = assets?.[0]?.wastedReason ?? '-'
    }

    if (wastedReason === '-') return wastedReason

    return (
      WASTED_REASON_LIST.find((reason) => reason.id === wastedReason)?.name ??
      wastedReason
    )
  }

  const mapped: MappedAssets[] = assets?.map((asset: AssetReportOutput) => {
    return {
      date: asset.createdAt ? dayjs(asset.createdAt).format(dateFormat) : '-',
      location: asset.surgery.visit?.location?.department ?? '-',
      caseId: asset.surgery.surgicalCase?.number ?? '-',
      patientName:
        asset.patient?.firstName && asset.patient?.lastName
          ? `${asset.patient.lastName}, ${asset.patient.firstName}`
          : '-',
      procedureType: asset.surgery?.procedures?.[0]?.description ?? '-',
      surgeon:
        asset.surgeon?.firstName || asset.surgeon?.lastName
          ? `${asset.surgeon?.firstName} ${asset.surgeon?.lastName}`
          : '-',
      manufacturer: asset.companyName ?? '-',
      productDescription: asset.deviceDescription ?? '-',
      modelNumber: asset.versionModelNumber ?? '-',
      lot: asset.lotBatch ?? '-',
      serial: asset.assets?.[0]?.serialNumber ?? '-',
      expiration: asset?.expirationDate
        ? dayjs(asset.expirationDate).format(dateFormat)
        : '-',
      udi: asset.assets?.[0]?.udi ?? '-',
      disposition: getImplantStatus(asset.assets as ReportAsset[]) ?? '-',
      wastedReason: getWastedReason(asset.assets as ReportAsset[]) ?? '-',
      assetType: getAssetTypeLabel(asset.assets?.[0]?.assetType) ?? '-',
      itemCost: asset.totalCost ?? 0,
    }
  })

  const handleDownload = async () => {
    try {
      if (!mapped || mapped.length === 0) {
        enqueueSnackbar('No data available to download', {
          variant: 'error',
        })
        return
      }

      const pdfDoc = pdf(<AssetsReportPDF assets={mapped} />)
      const blob = await pdfDoc.toBlob()
      saveAs(blob, 'assets-report.pdf')
      enqueueSnackbar('PDF generated successfully', {
        variant: 'success',
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      enqueueSnackbar('Error generating PDF', {
        variant: 'error',
      })
    }
  }

  return (
    <Box>
      <Header
        title="Product report"
        parent="Usage"
        actions={[
          {
            title: 'Download/Export',
            disabled: isLoading,
            icon: <DownloadIcon />,
            onClick: handleDownload,
          },
        ]}
      />
      <FilterBar
        renderDeviceDescription
        renderSurgeonFilter
        renderProcedureTypeFilter
        renderDatePicker
        renderAssetTypeFilter
        renderDepartmentFilter
        condenseButtons
        renderDisposition
        isLoading={isLoading}
      />
      <Box sx={{ px: 2 }}>
        <DataTable
          tableHeaders={headCells}
          tableRows={mapped || []}
          isLoading={isLoading}
          isErrored={isError}
        />
      </Box>
    </Box>
  )
}

export default AssetsReports
